import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Users, Plus, LogOut, UserPlus, Shield, Compass, X, Lock, Check, Globe, Bell, Search, HelpCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "../contexts/AuthContext";
import AvatarDisplay from "../components/AvatarDisplay";
import FriendsRoomCreate from "../components/FriendsRoomCreate";
import BottomNav from "../components/BottomNav";
import ReportIssueModal from "../components/ReportIssueModal";
import { io } from "socket.io-client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_5309bd56-751f-4453-bda3-7be8ecfcb781/artifacts/orcvyuj1_let%20see%20whos%20on.png";

// ── Smooth animated counter ────────────────────────────────────────────────────
function AnimatedCount({ value, className = "" }) {
  const [displayed, setDisplayed] = useState(value);
  const prevRef = useRef(value);
  useEffect(() => {
    const prev = prevRef.current;
    if (prev === value) return;
    prevRef.current = value;
    const diff = value - prev;
    const steps = 18;
    let step = 0;
    const id = setInterval(() => {
      step++;
      if (step >= steps) { setDisplayed(value); clearInterval(id); }
      else setDisplayed(Math.round(prev + (diff * step) / steps));
    }, 25);
    return () => clearInterval(id);
  }, [value]);
  return <span className={className}>{displayed}</span>;
}

// ── Cover image + category mapping ───────────────────────────────────────────
const COVER_IMAGES = {
  evening:  "https://images.pexels.com/photos/7590623/pexels-photo-7590623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lounge:   "https://images.pexels.com/photos/11279651/pexels-photo-11279651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  night:    "https://images.unsplash.com/photo-1504704911898-68304a7d2807?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  neon:     "https://images.unsplash.com/photo-1546640646-89b557854b23?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  social:   "https://images.unsplash.com/photo-1593958812614-2db6a598c71c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  gaming:   "https://images.unsplash.com/photo-1632603093711-0d93a0bcc6cc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  movie:    "https://images.unsplash.com/photo-1770520871616-d7379585fc9a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  dj:       "https://images.pexels.com/photos/15696528/pexels-photo-15696528.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  city:     "https://images.unsplash.com/photo-1515868769-ad822a0c67e9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
};

const BADGE = {
  CHAT:   "bg-purple-600/90 backdrop-blur-sm text-white",
  CHILL:  "bg-violet-500/90 backdrop-blur-sm text-white",
  MUSIC:  "bg-fuchsia-600/90 backdrop-blur-sm text-white",
  GAMING: "bg-emerald-600/90 backdrop-blur-sm text-white",
  MOVIES: "bg-blue-600/90 backdrop-blur-sm text-white",
  PARTY:  "bg-rose-500/90 backdrop-blur-sm text-white",
  NIGHT:  "bg-indigo-600/90 backdrop-blur-sm text-white",
  RANDOM: "bg-orange-500/90 backdrop-blur-sm text-white",
};

function getCardMeta(room, idx) {
  const t = `${room.name} ${room.description}`.toLowerCase();
  if (t.match(/gaming|game\b|esport|minecraft|xbox|playstation|steam|cod\b/))
    return { img: COVER_IMAGES.gaming,  label: "GAMING", badge: BADGE.GAMING };
  if (t.match(/movie|film|watch party|cinema|netflix|series|episode/))
    return { img: COVER_IMAGES.movie,   label: "MOVIES", badge: BADGE.MOVIES };
  if (t.match(/dj\b|rave|disco|club|dance|techno|house music/))
    return { img: COVER_IMAGES.dj,      label: "MUSIC",  badge: BADGE.MUSIC  };
  if (t.match(/music|indie|jazz|beats|playlist|lofi|lo-fi/))
    return { img: COVER_IMAGES.neon,    label: "MUSIC",  badge: BADGE.MUSIC  };
  if (t.match(/late night|midnight|night owl|3am|after dark/))
    return { img: COVER_IMAGES.night,   label: "NIGHT",  badge: BADGE.NIGHT  };
  if (t.match(/lounge|ambient|cosy|cozy|unwind|relax|soft/))
    return { img: COVER_IMAGES.lounge,  label: "CHILL",  badge: BADGE.CHILL  };
  if (t.match(/evening|coffee|cafe|warm|fireplace|quiet/))
    return { img: COVER_IMAGES.evening, label: "CHILL",  badge: BADGE.CHILL  };
  if (t.match(/party|festival|vibe|social|crowd/))
    return { img: COVER_IMAGES.social,  label: "PARTY",  badge: BADGE.PARTY  };
  if (t.match(/random|anything|stuff|general|whatever/))
    return { img: COVER_IMAGES.social,  label: "RANDOM", badge: BADGE.RANDOM };
  // Rotate fallback images by index so every card looks different
  const fallbacks = [COVER_IMAGES.evening, COVER_IMAGES.city, COVER_IMAGES.lounge,
                     COVER_IMAGES.night, COVER_IMAGES.neon, COVER_IMAGES.social];
  return { img: fallbacks[idx % fallbacks.length], label: "CHAT", badge: BADGE.CHAT };
}

// ── Room card ─────────────────────────────────────────────────────────────────
const NEW_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour

function RoomCard({ room, liveCount, onJoin, idx = 0, isOpen = true }) {
  const { img, label, badge } = getCardMeta(room, idx);
  // Admin-set cover photo overrides the keyword-matched default
  const coverImg = room.cover_photo || img;
  const isNew = isOpen && room.last_activity &&
    (Date.now() - new Date(room.last_activity).getTime()) < NEW_THRESHOLD_MS;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="bg-[#0B0B12] border border-white/5 rounded-2xl overflow-hidden hover:border-purple-500/40 hover:shadow-[0_8px_32px_rgba(139,92,246,0.18)] transition-all group cursor-pointer"
    >
      {/* ── Cover photo ────────────────────────────────────── */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={coverImg}
          alt={room.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          loading="lazy"
        />
        {/* Dark gradient: transparent top → solid bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/90" />

        {/* Category + NEW badges — stacked top-left */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold font-mono uppercase tracking-wider ${badge}`}>
            {label}
          </span>
          {isNew && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold font-mono uppercase tracking-wider bg-emerald-500/90 backdrop-blur-sm text-white"
              data-testid={`new-badge-${room.room_id}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse shrink-0" />
              NEW
            </motion.span>
          )}
        </div>

        {/* Live count — top-right */}
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-online" />
          <span className="text-[11px] text-emerald-300 font-mono font-semibold">
            <AnimatedCount value={liveCount} />
          </span>
        </div>

        {/* Room name on photo */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
          <h3 className="font-black text-white text-lg leading-tight font-[Outfit] drop-shadow-[0_2px_12px_rgba(0,0,0,1)]">
            {room.name}
          </h3>
        </div>
      </div>

      {/* ── Card body ──────────────────────────────────────── */}
      <div className="p-4 pt-3">
        <p className="text-xs text-slate-400 mb-4 leading-relaxed line-clamp-2">{room.description}</p>
        {isOpen ? (
          <button
            data-testid={`join-room-btn-${room.room_id}`}
            onClick={() => onJoin(room.room_id)}
            className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold text-sm rounded-xl hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2"
          >
            <MessageSquare className="w-4 h-4" /> JOIN CHAT
          </button>
        ) : (
          <div
            data-testid={`room-closed-btn-${room.room_id}`}
            className="w-full py-2.5 bg-[#141322] border border-slate-700 text-slate-500 text-sm rounded-xl flex items-center justify-center gap-2 cursor-not-allowed select-none"
          >
            🔒 Opens at 7pm
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Friends Room card ──────────────────────────────────────────────────────────
function FriendsRoomCard({ room, onEnter }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }}
      className="bg-[#0E0E17] border border-purple-500/20 rounded-2xl p-4 hover:border-purple-500/40 hover:shadow-[0_4px_20px_rgba(139,92,246,0.1)] transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600/25 to-cyan-500/25 border border-purple-500/20 flex items-center justify-center text-2xl shrink-0">
            {room.icon || "🏠"}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-bold text-white font-[Outfit] text-sm truncate">{room.name}</h3>
              <Lock className="w-3 h-3 text-purple-400 shrink-0" />
              {room.is_creator && (
                <span className="text-[10px] font-mono bg-cyan-500/15 text-cyan-400 px-1.5 py-0.5 rounded-full shrink-0">YOURS</span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">by {room.creator_alias}</p>
          </div>
        </div>
        {room.online_count > 0 && (
          <div className="flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-full shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-online" />
            <span className="text-xs text-emerald-400 font-mono font-bold">{room.online_count}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="flex -space-x-1.5">
            {(room.member_previews || []).slice(0, 4).map(m => (
              <AvatarDisplay key={m.user_id} avatarId={m.avatar_id} size="xs" className="ring-2 ring-[#0E0E17]" />
            ))}
          </div>
          <span className="text-xs text-slate-500 ml-1">{room.member_count} member{room.member_count !== 1 ? "s" : ""}</span>
        </div>
        <button data-testid={`enter-fr-btn-${room.room_id}`} onClick={() => onEnter(room.room_id)}
          className="px-4 py-1.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-xs font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all">
          {room.is_creator ? "OPEN" : "ENTER"}
        </button>
      </div>
    </motion.div>
  );
}

// ── Invitation card ────────────────────────────────────────────────────────────
function InvitationCard({ inv, onAccept, onDecline }) {
  const [loading, setLoading] = useState(false);
  const handle = async (action) => {
    setLoading(true);
    await action();
    setLoading(false);
  };
  return (
    <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
      className="flex items-center gap-3 bg-[#0E0E17] border border-amber-500/20 rounded-xl p-3">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600/25 to-cyan-500/25 flex items-center justify-center text-xl shrink-0">
        {inv.room_icon || "🏠"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{inv.room_name}</p>
        <p className="text-xs text-slate-400">
          from <span className="text-purple-300">{inv.inviter_alias}</span>
          {" · "}{inv.member_count} member{inv.member_count !== 1 ? "s" : ""}
        </p>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <button data-testid={`accept-fr-inv-${inv.invitation_id}`} disabled={loading}
          onClick={() => handle(() => onAccept(inv.invitation_id))}
          className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-semibold rounded-lg hover:bg-emerald-500/30 transition-colors disabled:opacity-40">
          <Check className="w-3 h-3" /> Accept
        </button>
        <button data-testid={`decline-fr-inv-${inv.invitation_id}`} disabled={loading}
          onClick={() => handle(() => onDecline(inv.invitation_id))}
          className="px-2.5 py-1.5 border border-slate-700 text-slate-400 text-xs rounded-lg hover:text-red-400 hover:border-red-500/30 transition-colors disabled:opacity-40">
          <X className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
}

// ── Room type picker ───────────────────────────────────────────────────────────
function RoomTypePicker({ onPublic, onFriends, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E0E17] border border-purple-500/25 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-white font-[Outfit]">Add a Room</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="room-type-public" onClick={onPublic}
            className="flex flex-col items-center gap-3 p-5 bg-[#141322] border border-slate-700 rounded-2xl hover:border-purple-500/50 hover:bg-[#1C1936] transition-all group">
            <Globe className="w-8 h-8 text-purple-400 group-hover:scale-110 transition-transform" />
            <div className="text-center">
              <p className="font-bold text-white text-sm font-[Outfit]">Public Room</p>
              <p className="text-xs text-slate-500 mt-1 leading-snug">Open to everyone. Submitted for admin review.</p>
            </div>
          </button>
          <button data-testid="room-type-friends" onClick={onFriends}
            className="flex flex-col items-center gap-3 p-5 bg-[#141322] border border-purple-500/30 rounded-2xl hover:border-purple-500/60 hover:bg-purple-500/10 transition-all group">
            <Lock className="w-8 h-8 text-cyan-400 group-hover:scale-110 transition-transform" />
            <div className="text-center">
              <p className="font-bold text-white text-sm font-[Outfit]">Friends Room</p>
              <p className="text-xs text-slate-500 mt-1 leading-snug">Private. Only friends you invite can join.</p>
            </div>
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Create room modal ─────────────────────────────────────────────────────────
function CreateRoomModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/rooms", form);
      onCreated(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create room");
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E0E17] border border-purple-500/30 rounded-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold text-white font-[Outfit]">Create a Room</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wide">Room Name</label>
            <input data-testid="room-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Evening Chat" required
              className="w-full bg-[#141322] border border-slate-700 rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wide">Description</label>
            <textarea data-testid="room-description-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What's this room about?" required rows={3}
              className="w-full bg-[#141322] border border-slate-700 rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500 resize-none" />
          </div>
          <p className="text-xs text-slate-500">Room will be reviewed by an admin before going live.</p>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-700 text-slate-300 rounded-xl text-sm hover:border-slate-500">Cancel</button>
            <button data-testid="create-room-submit-btn" type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold rounded-xl text-sm hover:opacity-90 disabled:opacity-50">
              {loading ? "Submitting..." : "Submit for Review"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ── Nav item ──────────────────────────────────────────────────────────────────
function NavItem({ icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
        active
          ? "bg-purple-500/15 border border-purple-500/25 text-white font-semibold"
          : "text-slate-400 hover:text-white hover:bg-[#141322]"
      }`}>
      <span className={active ? "text-purple-400" : ""}>{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      {badge > 0 && (
        <span className="w-5 h-5 rounded-full bg-cyan-500 text-white text-[10px] flex items-center justify-center font-bold">{badge}</span>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN LOBBY PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function LobbyPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showFrCreate, setShowFrCreate] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [roomCreated, setRoomCreated] = useState(false);
  const [totalOnline, setTotalOnline] = useState(1);
  const [liveRoomCounts, setLiveRoomCounts] = useState({});
  const [friendsRooms, setFriendsRooms] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [roomStatus, setRoomStatus] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const socketRef = useRef(null);
  const countdownRef = useRef(null);
  const notifySupported = typeof window !== "undefined" && "Notification" in window;

  // Init notification preference from localStorage
  useEffect(() => {
    if (notifySupported && Notification.permission === "granted") {
      setNotifyEnabled(localStorage.getItem("whoson_notify_7pm") === "1");
    }
  }, [notifySupported]);

  const triggerOpenedNotif = () => {
    if (notifySupported && Notification.permission === "granted" &&
        localStorage.getItem("whoson_notify_7pm") === "1") {
      new Notification("Who's On? is now open!", {
        body: "Rooms are live until 11pm. Drop in and see who's on.",
        icon: LOGO_URL,
      });
    }
  };

  const handleNotifyToggle = async () => {
    if (notifyEnabled) {
      localStorage.removeItem("whoson_notify_7pm");
      setNotifyEnabled(false);
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      localStorage.setItem("whoson_notify_7pm", "1");
      setNotifyEnabled(true);
      new Notification("You\u2019re set!", {
        body: "We\u2019ll notify you when rooms open at 7pm.",
        icon: LOGO_URL,
      });
    }
  };

  // Total notification count = pending friend requests + pending room invitations
  const notifCount = friendRequestCount + invitations.length;

  useEffect(() => {
    fetchData();
    fetchRoomStatus();
    socketRef.current = io(BACKEND_URL, { path: "/api/socket.io", withCredentials: true, transports: ["websocket", "polling"] });
    socketRef.current.on("user_status_change", () => fetchOnlineUsers());
    socketRef.current.on("stats_update", ({ online_count, room_counts }) => {
      setTotalOnline(online_count || 1);
      setLiveRoomCounts(room_counts || {});
    });
    socketRef.current.on("fr_kicked", ({ room_id }) => setFriendsRooms(prev => prev.filter(r => r.room_id !== room_id)));
    socketRef.current.on("fr_deleted", ({ room_id }) => setFriendsRooms(prev => prev.filter(r => r.room_id !== room_id)));
    socketRef.current.on("room_status_update", (s) => setRoomStatus(s));
    socketRef.current.on("rooms_opened",  (s) => { setRoomStatus(s); triggerOpenedNotif(); });
    socketRef.current.on("rooms_closed",  (s) => setRoomStatus(s));
    socketRef.current.on("new_friend_request", ({ from_alias }) => {
      setFriendRequestCount(n => n + 1);
      toast(`${from_alias} wants to be friends`, {
        description: "Tap to view your friend requests",
        action: { label: "View", onClick: () => navigate("/friends") },
        duration: 8000,
        icon: "👋",
      });
    });
    // Admin: real-time auto-moderation alert
    socketRef.current.on("auto_flag_alert", ({ alias, reason, auto_deleted }) => {
      toast.warning(`🤖 Auto-flagged: ${alias}`, {
        description: `${reason}${auto_deleted ? " · Message auto-deleted" : " · Review needed"}`,
        action: { label: "Review", onClick: () => navigate("/admin") },
        duration: 15000,
      });
    });

    // Live countdown to opening when rooms are closed
    const tickCountdown = () => {
      const pad = n => String(n).padStart(2, "0");
      const now = new Date();
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      }).formatToParts(now);
      const h = parseInt(parts.find(p => p.type === "hour")?.value   ?? "0");
      const m = parseInt(parts.find(p => p.type === "minute")?.value ?? "0");
      const s = parseInt(parts.find(p => p.type === "second")?.value ?? "0");
      const total = h * 3600 + m * 60 + s;
      const OPEN = 19 * 3600, CLOSE = 23 * 3600;
      if (total >= OPEN && total < CLOSE) { setCountdown(""); return; }
      const diff = total < OPEN ? OPEN - total : (86400 - total) + OPEN;
      const dh = Math.floor(diff / 3600);
      const dm = Math.floor((diff % 3600) / 60);
      const ds = diff % 60;
      setCountdown(dh > 0 ? `${dh}h ${pad(dm)}m ${pad(ds)}s` : `${pad(dm)}m ${pad(ds)}s`);
    };
    tickCountdown();
    countdownRef.current = setInterval(tickCountdown, 1000);
    const interval = setInterval(() => { fetchData(); fetchRoomStatus(); }, 20000);
    return () => {
      clearInterval(interval);
      clearInterval(countdownRef.current);
      if (socketRef.current?.connected) socketRef.current.disconnect();
    };
  }, []);

  const fetchData = () => { fetchRooms(); fetchOnlineUsers(); fetchFriendsRooms(); fetchInvitations(); fetchFriendRequests(); };
  const fetchRooms = async () => { try { const { data } = await api.get("/rooms"); setRooms(data); } catch {} };
  const fetchOnlineUsers = async () => { try { const { data } = await api.get("/users/online"); setOnlineUsers(data); } catch {} };
  const fetchFriendsRooms = async () => { try { const { data } = await api.get("/friends-rooms"); setFriendsRooms(data); } catch {} };
  const fetchInvitations = async () => { try { const { data } = await api.get("/friends-rooms/invitations"); setInvitations(data); } catch {} };
  const fetchFriendRequests = async () => {
    try { const { data } = await api.get("/friends"); setFriendRequestCount((data.pending_requests || []).length); } catch {}
  };
  const fetchRoomStatus = async () => {
    try { const { data } = await api.get("/rooms/status"); setRoomStatus(data); } catch {}
  };

  const handleAcceptInv = async (invId) => {
    try {
      const { data } = await api.post(`/friends-rooms/invitations/${invId}/accept`);
      setInvitations(prev => prev.filter(i => i.invitation_id !== invId));
      fetchFriendsRooms();
      navigate(`/friends-room/${data.room_id}`);
    } catch {}
  };
  const handleDeclineInv = async (invId) => {
    try {
      await api.post(`/friends-rooms/invitations/${invId}/decline`);
      setInvitations(prev => prev.filter(i => i.invitation_id !== invId));
    } catch {}
  };
  const handleLogout = async () => { await logout(); navigate("/", { replace: true }); };
  const handleRoomCreated = (room) => {
    setShowCreate(false);
    if (room.status === "pending") { setRoomCreated(true); setTimeout(() => setRoomCreated(false), 4000); }
    else fetchRooms();
  };

  const handleAddFriendFromLobby = async (userId, alias) => {
    try {
      await api.post(`/friends/request/${userId}`);
      // Optimistically update friendship_status in onlineUsers list
      setOnlineUsers(prev => prev.map(u => u.user_id === userId ? { ...u, friendship_status: "pending" } : u));
    } catch (err) {
      // silently ignore if already friends/pending
    }
  };

  return (
    <div className="min-h-screen bg-[#07070B] flex">

      {/* ── Left Sidebar ────────────────────────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-56 lg:w-64 shrink-0 bg-[#09090F] border-r border-purple-500/20 sticky top-0 h-screen overflow-y-auto">
        {/* Logo — large and prominent */}
        <div className="flex flex-col items-center px-4 pt-6 pb-5 border-b border-purple-500/10">
          <img
            src={LOGO_URL}
            alt="Let's Check Who's On?"
            className="w-40 lg:w-44 h-auto drop-shadow-[0_0_28px_rgba(139,92,246,0.55)]"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          <NavItem icon={<Compass className="w-4 h-4" />} label="Explore Rooms" active />
          <NavItem icon={<UserPlus className="w-4 h-4" />} label="Friends"
            badge={friendRequestCount} onClick={() => navigate("/friends")} />
          <NavItem
            icon={<Bell className="w-4 h-4" />}
            label="Notifications"
            badge={notifCount}
            onClick={() => { if (notifCount > 0) { document.getElementById("invitations-section")?.scrollIntoView({ behavior: "smooth" }); } }}
          />
          {user?.role === "admin" && (
            <NavItem icon={<Shield className="w-4 h-4" />} label="Admin Panel"
              onClick={() => navigate("/admin")} />
          )}
        </nav>

        {/* Report an Issue */}
        <div className="px-3 pb-2">
          <button
            data-testid="report-issue-sidebar-btn"
            onClick={() => setShowReportIssue(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-slate-500 hover:text-slate-300 hover:bg-[#141322] text-xs transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Report an Issue
          </button>
        </div>

        {/* Online count */}
        <div className="mx-3 mb-2 bg-[#0E0E17] border border-purple-500/15 rounded-xl p-3">
          <div className="flex items-center gap-2">
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-sm font-semibold text-white">
              <AnimatedCount value={totalOnline} />
            </span>
            <span className="text-xs text-slate-400">online now</span>
          </div>
        </div>

        {/* User info */}
        <div className="p-3 border-t border-purple-500/10">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl">
            <AvatarDisplay avatarId={user?.avatar_id} size="sm" status="online" showStatus />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.alias}</p>
              <p className="text-xs text-emerald-400">Online</p>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout} className="text-slate-500 hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Content area ────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header — simplified, nav is in bottom bar */}
        <header className="md:hidden sticky top-0 z-40 h-14 bg-[#0E0E17]/90 backdrop-blur-xl border-b border-purple-500/20 flex items-center justify-between px-4">
          <img src={LOGO_URL} alt="Who's On?" className="h-9 w-auto drop-shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
          <div className="flex items-center gap-3">
            {/* Notification bell */}
            <button
              data-testid="notification-bell-btn"
              onClick={() => { if (notifCount > 0) document.getElementById("invitations-section")?.scrollIntoView({ behavior: "smooth" }); }}
              className="relative text-slate-400 hover:text-white transition-colors"
            >
              <Bell className="w-5 h-5" />
              {notifCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
            <span className="flex items-center gap-1.5 text-sm text-slate-300">
              <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-online" />
              {totalOnline}
            </span>
            <button data-testid="create-room-btn-mobile" onClick={() => setShowTypePicker(true)}
              className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 flex items-center justify-center text-white hover:opacity-90">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {/* Main scrollable area */}
          <main className="flex-1 overflow-y-auto p-4 md:p-5 pb-24 md:pb-5">
            {/* Welcome row */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-white font-[Outfit]">Hey, {user?.alias}!</h2>
                <p className="text-slate-400 text-sm mt-0.5">Here's who's on right now.</p>
              </div>
              <button data-testid="create-room-btn" onClick={() => setShowTypePicker(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold text-sm rounded-xl hover:opacity-90 active:scale-95 transition-all">
                <Plus className="w-4 h-4" /> New Room
              </button>
            </div>

            {/* ── Evening schedule status banner ── */}
            {roomStatus && (
              <motion.div
                data-testid="room-schedule-banner"
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                className={`mb-4 flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border text-sm ${
                  roomStatus.is_open
                    ? roomStatus.warning
                      ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
                      : "bg-emerald-500/8 border-emerald-500/25 text-emerald-200"
                    : "bg-[#141322] border-purple-500/15 text-slate-400"
                }`}
              >
                <span className="text-base shrink-0">
                  {roomStatus.is_open ? (roomStatus.warning ? "⏰" : "🟢") : "🔒"}
                </span>
                <span className="flex-1 min-w-0">
                  {roomStatus.warning || roomStatus.message}
                </span>
                {!roomStatus.is_open && countdown && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-600">Opens in</span>
                    <span
                      data-testid="lobby-countdown"
                      className="font-mono text-lg font-black text-white tracking-tight"
                    >
                      {countdown}
                    </span>
                  </div>
                )}
                {/* Notify me button — only shown when rooms are closed */}
                {!roomStatus.is_open && notifySupported && (
                  <button
                    data-testid="lobby-notify-btn"
                    onClick={handleNotifyToggle}
                    title={notifyEnabled ? "Turn off 7pm notification" : "Get notified when rooms open"}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all shrink-0 ${
                      notifyEnabled
                        ? "bg-purple-500/20 border-purple-500/40 text-purple-300"
                        : "border-slate-700 text-slate-500 hover:border-purple-500/30 hover:text-slate-300"
                    }`}
                  >
                    <Bell className="w-3.5 h-3.5" />
                    {notifyEnabled ? "Notifying" : "Notify me"}
                  </button>
                )}
              </motion.div>
            )}

            {/* ── Pending invitations ── */}
            <AnimatePresence>
              {invitations.length > 0 && (
                <motion.div id="invitations-section" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-xs uppercase tracking-widest text-amber-400">Invitations</span>
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-[10px] text-white flex items-center justify-center font-bold">{invitations.length}</span>
                  </div>
                  <div className="space-y-2">
                    {invitations.map(inv => (
                      <InvitationCard key={inv.invitation_id} inv={inv}
                        onAccept={handleAcceptInv} onDecline={handleDeclineInv} />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Live Ticker */}
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="mb-5 bg-[#0E0E17] border border-purple-500/20 rounded-2xl px-4 py-3 flex items-center gap-4 overflow-x-auto scrollbar-thin"
              data-testid="live-ticker">
              <div className="flex items-center gap-2 shrink-0">
                <span className="relative flex w-2.5 h-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold">LIVE</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 border-r border-slate-700 pr-4">
                <AnimatedCount value={totalOnline} className="text-2xl font-black text-white font-[Outfit]" />
                <span className="text-xs text-slate-400 leading-tight">chatting<br/>now</span>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin">
                {rooms.map(r => (
                  <motion.button key={r.room_id} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    onClick={() => navigate(`/room/${r.room_id}`)}
                    className="flex items-center gap-2 shrink-0 bg-[#141322] hover:bg-[#1C1936] border border-purple-500/15 hover:border-purple-500/40 rounded-xl px-3 py-1.5 transition-all">
                    <span className={`w-1.5 h-1.5 rounded-full ${(liveRoomCounts[r.room_id] || 0) > 0 ? "bg-emerald-400 pulse-online" : "bg-slate-600"}`} />
                    <span className="text-xs text-slate-300 font-medium max-w-[90px] truncate">{r.name}</span>
                    <span className="text-xs font-mono font-bold text-cyan-400 min-w-[14px] text-center">
                      <AnimatedCount value={liveRoomCounts[r.room_id] || 0} />
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            <AnimatePresence>
              {roomCreated && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-300 text-sm">
                  Room submitted for review! An admin will approve it soon.
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mb-3 flex items-center gap-2">
              <span className="font-mono text-xs uppercase tracking-widest text-cyan-400">Live Rooms</span>
              <span className="text-xs text-slate-500">{rooms.length} active</span>
            </div>

            {/* Room search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
              <input
                data-testid="room-search-input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search rooms..."
                className="w-full bg-[#0E0E17] border border-purple-500/15 rounded-xl py-2.5 pl-10 pr-9 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500/50 transition-colors"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {(() => {
              const filtered = searchQuery.trim()
                ? rooms.filter(r =>
                    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    r.description.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                : rooms;
              return filtered.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>{searchQuery ? `No rooms matching "${searchQuery}"` : "No rooms active right now"}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="rooms-grid">
                  {filtered.map((room, i) => (
                    <RoomCard key={room.room_id} room={room} idx={i}
                      isOpen={roomStatus?.is_open !== false}
                      liveCount={liveRoomCounts[room.room_id] ?? room.member_count ?? 0}
                      onJoin={(id) => navigate(`/room/${id}`)} />
                  ))}
                </div>
              );
            })()}

            {/* ── MY FRIENDS ROOMS ── */}
            {friendsRooms.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-3.5 h-3.5 text-purple-400" />
                  <span className="font-mono text-xs uppercase tracking-widest text-purple-400">My Friends Rooms</span>
                  <span className="text-xs text-slate-500">{friendsRooms.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="friends-rooms-grid">
                  {friendsRooms.map(room => (
                    <FriendsRoomCard key={room.room_id} room={room}
                      onEnter={(id) => navigate(`/friends-room/${id}`)} />
                  ))}
                </div>
              </div>
            )}

            {/* Mobile: Report an Issue link */}
            <div className="md:hidden mt-8 text-center">
              <button
                data-testid="report-issue-mobile-btn"
                onClick={() => setShowReportIssue(true)}
                className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-xs transition-colors"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Report an Issue
              </button>
            </div>
          </main>

          {/* Right sidebar — Who's On */}
          <aside className="hidden lg:flex flex-col w-64 shrink-0 border-l border-purple-500/15 overflow-y-auto p-4">
            <div className="bg-[#0E0E17] border border-purple-500/15 rounded-2xl p-4 sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-semibold text-white font-[Outfit]">Who's On</span>
                <span className="ml-auto text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                  <AnimatedCount value={onlineUsers.length + 1} />
                </span>
              </div>

              {/* Current user */}
              <div className="flex items-center gap-2.5 p-2 rounded-xl bg-purple-500/10 mb-2">
                <AvatarDisplay avatarId={user?.avatar_id} size="sm" status="online" showStatus />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{user?.alias}</p>
                  <p className="text-xs text-emerald-400">You · in the lobby</p>
                </div>
              </div>

              {/* Online users with room + add friend */}
              <div className="space-y-0.5 max-h-96 overflow-y-auto scrollbar-thin">
                {onlineUsers.map(u => (
                  <div key={u.user_id} data-testid={`online-user-${u.user_id}`}
                    className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[#141322] transition-colors group">
                    <AvatarDisplay avatarId={u.avatar_id} size="sm" status={u.status} showStatus />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-200 truncate font-medium">{u.alias}</p>
                      {u.current_room_name ? (
                        <button
                          data-testid={`join-friend-room-${u.user_id}`}
                          onClick={() => navigate(`/room/${u.current_room_id}`)}
                          className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-0.5 truncate"
                        >
                          {u.current_room_name}
                          <ChevronRight className="w-3 h-3 shrink-0" />
                        </button>
                      ) : (
                        <p className="text-xs text-slate-500">in the lobby</p>
                      )}
                    </div>
                    {/* Add friend button — shows on hover */}
                    {u.friendship_status === "none" && (
                      <button
                        data-testid={`lobby-add-friend-${u.user_id}`}
                        onClick={() => handleAddFriendFromLobby(u.user_id, u.alias)}
                        title={`Add ${u.alias} as a friend`}
                        className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full border border-purple-500/30 flex items-center justify-center text-purple-400 hover:bg-purple-500/20 transition-all shrink-0"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {u.friendship_status === "friends" && (
                      <span className="opacity-0 group-hover:opacity-100 text-[9px] text-emerald-400 font-mono shrink-0 transition-opacity">FRIEND</span>
                    )}
                    {u.friendship_status === "pending" && (
                      <span className="opacity-0 group-hover:opacity-100 text-[9px] text-amber-400 font-mono shrink-0 transition-opacity">SENT</span>
                    )}
                  </div>
                ))}
                {onlineUsers.length === 0 && (
                  <p className="text-xs text-slate-600 text-center py-4">Just you so far!</p>
                )}
              </div>

              <button data-testid="view-friends-btn" onClick={() => navigate("/friends")}
                className="w-full mt-3 py-2 text-xs text-purple-400 hover:text-purple-300 border border-purple-500/20 rounded-xl hover:border-purple-500/40 transition-colors">
                View Friends
              </button>
            </div>
          </aside>
        </div>
      </div>

      {showCreate && <CreateRoomModal onClose={() => setShowCreate(false)} onCreated={handleRoomCreated} />}
      {showTypePicker && (
        <RoomTypePicker
          onClose={() => setShowTypePicker(false)}
          onPublic={() => { setShowTypePicker(false); setShowCreate(true); }}
          onFriends={() => { setShowTypePicker(false); setShowFrCreate(true); }}
        />
      )}
      {showFrCreate && (
        <FriendsRoomCreate
          onClose={() => setShowFrCreate(false)}
          onCreated={(room) => { fetchFriendsRooms(); }}
        />
      )}

      {/* Report Issue Modal */}
      <AnimatePresence>
        {showReportIssue && <ReportIssueModal onClose={() => setShowReportIssue(false)} />}
      </AnimatePresence>

      {/* Mobile bottom navigation */}
      <BottomNav active="rooms" />
    </div>
  );
}
